import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Table, TableBody, TableContainer } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLoanEditorRunnerContext } from 'app/pages/loanApplication/LoanHeaders/LoanInfoHeader/LoanActionsControls';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { ConditionKey } from 'constants/condition';
import useLoanId from 'hooks/useLoanId';
import useSort from 'hooks/useSort';
import { checkIsDarkTheme } from 'utils/theme';

import { selectIsBrokerCompany, selectIsSubCompanyUser } from 'store/auth/selectors';
import { getLoanConditionByLoanIdAction } from 'store/loanApplications/actions';

import { UnderwritingTestIds } from '../../../Underwriting.testIds';

import LoanConditionTableHead from './LoanConditionTableHead';
import LoanConditionTableItem from './LoanConditionTableItem';

const defaultArray = [];

const defaultOrder = {
  direction: 'desc',
  id: ConditionKey.SIGNED_OFF,
};

const getStyles = theme => {
  const { palette } = theme;
  const isDarkTheme = checkIsDarkTheme(palette.type);

  return {
    table: {
      borderRadius: '10px',
      padding: '0 10px',
      backgroundColor: isDarkTheme ? palette.grey.A400 : palette.common.white,
      '& .MuiTableCell-head': {
        padding: '16px 10px',
      },
    },
  };
};

const LoanConditionTable = ({ conditions = defaultArray, isLoading }) => {
  const theme = useTheme();
  const classes = getStyles(theme);

  const dispatch = useDispatch();
  const loanId = useLoanId();
  const loanEditorRunnerContext = useLoanEditorRunnerContext();

  const { order, onRequestSort } = useSort(defaultOrder);

  const isSubCompanyUser = useSelector(selectIsSubCompanyUser);
  const isBrokerCompany = useSelector(selectIsBrokerCompany);

  const onTaskActionRefresh = useCallback(
    () => dispatch(getLoanConditionByLoanIdAction({ loanId, withDeleted: true })),
    [dispatch, loanId],
  );

  const orderedConditions = useMemo(() => {
    if (order.id === ConditionKey.SIGNED_OFF) {
      return _.orderBy(
        conditions,
        [item => !item.signedOff && item.canBeSignedOff, item => !item.signedOff, 'number'],
        [order.direction, order.direction, order.direction],
      );
    }

    return _.orderBy(
      conditions,
      [
        condition => {
          switch (order.id) {
            case ConditionKey.NUMBER: {
              return Number(condition.number);
            }

            case ConditionKey.CREATED_DATE:
              return new Date(condition.createdDate);

            case ConditionKey.LAST_MODIFIED:
              return !!condition.isDeleted && new Date(condition.lastModified);

            default:
              return condition[order.id]?.toString()?.toLowerCase();
          }
        },
      ],
      [order.direction],
    );
  }, [conditions, order]);

  const {
    withDeletedDateCell,
    withRejectedDateCell,
    withSignedOffCell,
    withActionsCell,
    withCollapseCell,
  } = useMemo(
    () =>
      orderedConditions.reduce(
        (acc, condition) => {
          const isTerminal = !!condition.isDeleted || !!condition.rejectedDate;

          if (!acc.withDeletedDateCell) {
            acc.withDeletedDateCell = !!condition.isDeleted;
          }

          if (!acc.withRejectedDateCell) {
            acc.withRejectedDateCell = !!condition.rejectedDate;
          }

          if (!acc.withSignedOffCell) {
            acc.withSignedOffCell = !!condition.signedOff;
          }

          if (!acc.withActionsCell) {
            acc.withActionsCell = !isSubCompanyUser && !isTerminal;
          }

          if (!acc.withCollapseCell) {
            acc.withCollapseCell =
              isBrokerCompany || (!condition.isDeleted && !!condition.taskIds?.length);
          }

          return acc;
        },
        {
          withDeletedDateCell: false,
          withRejectedDateCell: false,
          withSignedOffCell: false,
          withActionsCell: false,
          withCollapseCell: false,
        },
      ),
    [orderedConditions, isBrokerCompany, isSubCompanyUser],
  );

  return (
    <TableContainer sx={classes.table}>
      <Table data-testid={UnderwritingTestIds.LOAN_CONDITION_TABLE}>
        <LoanConditionTableHead
          isLoading={isLoading}
          order={order}
          withActionsCell={withActionsCell}
          withCollapseCell={withCollapseCell}
          withDeletedDateCell={withDeletedDateCell}
          withRejectedDateCell={withRejectedDateCell}
          withSignedOffCell={withSignedOffCell}
          onRequestSort={onRequestSort}
        />
        <TableBody>
          {orderedConditions.map(condition => (
            <LoanConditionTableItem
              key={condition.id}
              {...condition}
              taskActionRunnerContext={loanEditorRunnerContext}
              withActionsCell={withActionsCell}
              withCollapseCell={withCollapseCell}
              withDeletedDateCell={withDeletedDateCell}
              withRejectedDateCell={withRejectedDateCell}
              withSignedOffCell={withSignedOffCell}
              onTaskActionRefresh={onTaskActionRefresh}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

LoanConditionTable.propTypes = {
  ...WithLoadingPropTypes,
  conditions: PropTypes.array,
};

export default compose(withLoading, memo)(LoanConditionTable);
